import { AppShell, Button, Center, Divider, Flex, Image, Menu, Text, Tooltip } from '@mantine/core';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { FullLogoIcon } from 'src/UI/icons';
import SquareLogo from 'src/UI/icons/logo/SquareLogo';
import classes from './HeaderRevamp.module.css';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { Logger } from 'src/utils/Logger';
import { useLogout, useNavigation } from '@refinedev/core';
import { showError } from 'src/notifications/notifications';
import { ClientTabs } from 'src/home/ClientData';
import { IconLogout, IconMoodEdit, IconSettings, IconSun } from '@tabler/icons-react';
import React, { memo, useMemo } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { CreateButton } from 'src/common/components/CreateButton';
import { useFlags } from 'src/feature-flags/FlagsHook';

export const HEADER_HEIGHT = 72;

function NavigableLogo() {
  const { push } = useNavigation();

  return <LogoWithIcon onClick={() => push('/')} />;
}

export function LogoWithIcon({
  onClick,
  textColour = 'white',
}: { onClick?: () => void; textColour?: string } = {}) {
  return (
    <Flex
      align="center"
      h="100%"
      gap="xxs"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'auto' }}
    >
      <SquareLogo />
      <FullLogoIcon height={34} color={textColour} />
    </Flex>
  );
}

export default function Header({
  showChat,
  isChatOpened,
  bg = 'navBackground',
}: {
  showChat: () => void;
  isChatOpened: boolean;
  bg?: string;
}) {
  const { push } = useNavigation();
  const { clientId } = useCurrentClientIfAvailable();
  const { isCrawford, isAfternoon } = useIsAfternoon();
  const { currentUser } = useCurrentUser();
  const { mutate: logout } = useLogout({
    mutationOptions: {
      onError: error => {
        Logger.error('Log out failed', error);
        showError('Something went wrong while trying to log you out. Please try again.');
      },
    },
  });
  const { flagEnabledMeetings } = useFlags();

  const clientTabsRegex = useMemo(() => {
    const tabNames = Object.values(ClientTabs).join('|');
    return new RegExp(`/client/${clientId}/(${tabNames}).*`);
  }, [clientId]);

  const isUserAdmin = currentUser?.userRole === 'SYSTEM_ADMIN';

  return (
    <AppShell.Header withBorder={false} className={classes.headerContainer} bg={bg}>
      <Flex align="center" h="100%" px="xxl" justify="space-between">
        <Flex h="100%" align="center" gap="44px">
          <NavigableLogo />
          <Flex align="center" h="100%" gap="sm">
            <MenuItem title="All clients" href="/clients/all" activeRegex={clientTabsRegex} />
            <MenuItem title="Company data" href="/company" activeRegex={/^\/company\/.*$/} />
            {flagEnabledMeetings && (
              <MenuItem title="Meetings" href="/meetings" activeRegex={/^\/meetings\/.*$/} />
            )}
          </Flex>
        </Flex>

        <Flex align="center" h="100%" gap="xxl">
          <Flex align="center" h="100%" gap="sm">
            {isAfternoon && (
              <NavLogo label={'Secret menu'} onClick={() => push('/secret-menu')}>
                <IconSun stroke={1} color={'white'} />
              </NavLogo>
            )}
            <CreateButton buttonProps={{ h: 40, variant: 'filled-pill', bg: 'brand.6' }} />
            <Button
              h={40}
              variant="filled-pill"
              bg={isChatOpened ? 'gray.3' : 'white'}
              onClick={showChat}
              disabled={isChatOpened}
              leftSection={
                <img
                  src="/images/sparkle-icon.png"
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                  }}
                />
              }
            >
              Ask Siesta AI
            </Button>
          </Flex>

          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <Image
                style={{
                  width: 40,
                  height: 40,
                }}
                radius="xl"
                src={
                  isCrawford
                    ? 'https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/crawford.jpeg'
                    : `https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${currentUser?.firstName} + ${currentUser?.lastName}`
                }
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Welcome, {currentUser?.firstName}!</Menu.Label>

              <Menu.Item
                leftSection={<IconMoodEdit size={16} />}
                onClick={() => {
                  push(`/user/details/${currentUser?.id}`);
                }}
              >
                My Details
              </Menu.Item>

              <Divider />

              {isUserAdmin && (
                <>
                  <Menu.Label>Admin settings</Menu.Label>
                  <Menu.Item
                    leftSection={<IconSettings size={16} />}
                    onClick={() => push('/admin/users')}
                  >
                    User Management
                  </Menu.Item>

                  <Divider />
                </>
              )}

              <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => logout()}>
                Log out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>
    </AppShell.Header>
  );
}

const MenuItem = memo(function MenuItem({
  title,
  href,
  activeRegex,
}: {
  title: string;
  href: string;
  activeRegex?: RegExp;
}) {
  const isActive = useIsActive(href, activeRegex);

  return (
    <NavLink to={href} className={classes.menuItem} data-active={isActive}>
      <Text size="md" fw={600} px="xxs" py="xxxs" style={{ textWrap: 'nowrap' }}>
        {title}
      </Text>
    </NavLink>
  );
});

function useIsActive(href: string, activeRegex?: RegExp) {
  const pathMatch = useMatch(href);
  const { pathname } = useLocation();

  if (activeRegex != null && activeRegex.test(pathname)) {
    return true;
  }

  return pathMatch != null;
}

type NavLogoProps = React.PropsWithChildren<{
  onClick?: () => void;
  label: string;
}>;

function NavLogo({ children, onClick, label }: NavLogoProps) {
  return (
    <Tooltip label={label} withArrow position={'right'} color="white" c={'black'}>
      <Center onClick={onClick} w={40} h={40} className={classes.pointer}>
        {children}
      </Center>
    </Tooltip>
  );
}
